import React, { useState, useEffect, useCallback } from 'react';
import { salesAPI } from '../../services/api';
import { theme } from '../../theme';

const INPUT = {
  border: `1px solid ${theme.border}`, borderRadius: '0.5rem',
  padding: '0.5rem 0.75rem', fontSize: '0.875rem',
  color: theme.text, background: '#fff', outline: 'none', boxSizing: 'border-box',
};

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editVendor, setEditVendor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    salesAPI.getVendors(params)
      .then(r => { setVendors(r.data.vendors || []); setTotal(r.data.total || 0); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    await salesAPI.deleteVendor(id);
    load();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Vendors / OEMs</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>{total} vendor{total !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={() => { setEditVendor(null); setShowForm(true); }}
          style={{ background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}
        >
          + Add Vendor
        </button>
      </div>

      <input
        type="text"
        placeholder="Search vendors…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ ...INPUT, width: '16rem' }}
      />

      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>Loading…</div>
        ) : vendors.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted }}>No vendors found</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: theme.pageBg, borderBottom: `1px solid ${theme.border}` }}>
                  {['Name', 'Contact Person', 'Email', 'Phone', 'Products/Services', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vendors.map((v, i) => (
                  <tr
                    key={v.id}
                    style={{ borderBottom: `1px solid ${theme.border}`, background: i % 2 === 0 ? '#fff' : theme.pageBg }}
                    onMouseEnter={e => e.currentTarget.style.background = theme.lightBg}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : theme.pageBg}
                  >
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 500, color: theme.text }}>{v.name}</td>
                    <td style={{ padding: '0.75rem 1rem', color: theme.textMuted }}>{v.contact_person || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: theme.textMuted }}>{v.email || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: theme.textMuted }}>{v.phone || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem', color: theme.textMuted, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.products_services || '—'}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button
                          onClick={() => { setEditVendor(v); setShowForm(true); }}
                          style={{ background: 'none', border: 'none', fontSize: '0.8rem', color: theme.primary, cursor: 'pointer', padding: 0 }}
                        >Edit</button>
                        <button
                          onClick={() => handleDelete(v.id)}
                          style={{ background: 'none', border: 'none', fontSize: '0.8rem', color: theme.danger, cursor: 'pointer', padding: 0 }}
                        >Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <VendorModal
          vendor={editVendor}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function VendorModal({ vendor, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: vendor?.name || '',
    contact_person: vendor?.contact_person || '',
    email: vendor?.email || '',
    phone: vendor?.phone || '',
    address: vendor?.address || '',
    gst_number: vendor?.gst_number || '',
    pan_number: vendor?.pan_number || '',
    products_services: vendor?.products_services || '',
    notes: vendor?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (vendor) {
        await salesAPI.updateVendor(vendor.id, form);
      } else {
        await salesAPI.createVendor(form);
      }
      onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: '1rem', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem', borderBottom: `1px solid ${theme.border}` }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: theme.text, margin: 0 }}>{vendor ? 'Edit Vendor' : 'Add Vendor'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: theme.textMuted, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <form onSubmit={save} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Vendor Name *</label>
              <input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
            </div>
            {[
              { key: 'contact_person', label: 'Contact Person' },
              { key: 'email', label: 'Email', type: 'email' },
              { key: 'phone', label: 'Phone' },
              { key: 'gst_number', label: 'GST Number' },
              { key: 'pan_number', label: 'PAN Number' },
            ].map(f => (
              <div key={f.key}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>{f.label}</label>
                <input type={f.type || 'text'} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
              </div>
            ))}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Address</label>
              <input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} style={{ ...INPUT, width: '100%' }} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' }}>Products / Services</label>
              <textarea rows={2} value={form.products_services} onChange={e => setForm(p => ({ ...p, products_services: e.target.value }))} style={{ ...INPUT, width: '100%', resize: 'none' }} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '0.5rem' }}>
            <button type="button" onClick={onClose}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', color: theme.textMuted, border: `1px solid ${theme.border}`, borderRadius: '0.5rem', background: '#fff', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : (vendor ? 'Update' : 'Add Vendor')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
